import React, { useState, Fragment } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { countries, getCountryPricing } from "./Countries";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";

import { useLocation } from "react-router-dom";
import { calculatorLocale, readPreferences } from "./calculatorLocale";
import "./WaCalculator.css";

// Meta gives 1,000 free service messages per phone number per month
const FREE_SERVICE_MESSAGES = 1000;

// Used when a customer has no agreed processing fee for a message type
const DEFAULT_PROCESSING_FEES = {
  Marketing: 0.15,
  Utility: 0.15,
  Authentication: 0.15,
  Authentication_International: 0.15,
  Service: 0.03,
};

const MESSAGE_TYPES = [
  { type: "Marketing", inputLabel: "Marketing Message", costLabel: "Marketing Message Cost" },
  { type: "Utility", inputLabel: "Utility Message", costLabel: "Utility Message Cost" },
  { type: "Authentication", inputLabel: "Authentication Message", costLabel: "Authentication Message Cost" },
  {
    type: "Authentication_International",
    inputLabel: "Authentication – International Message",
    costLabel: "Authentication – International Message Cost",
  },
  { type: "Service", inputLabel: "Service Message", costLabel: "Service Message Cost" },
];

function PricingBreakdown({ pricing, locale }) {
  const { t, number, money } = locale;
  if (!pricing) {
    return null;
  }
  if (pricing.appliedTiers) {
    return (
      <div className="text-xs calc-muted mt-1">
        <div className="font-semibold">{t("Tiered Breakdown:")}</div>
        {pricing.appliedTiers.map((tier, index) => (
          <div key={index} className="calc-ms-2 mt-1">
            {number(tier.messages)} {t("messages")} (<bdi>{locale.range(tier.tier_range)}</bdi>):
            <bdi>{money(tier.rate)}</bdi>/{t("msg")} = <bdi>{money(tier.cost)}</bdi>
            {tier.discount && (
              <span className="calc-success"> ({number(parseFloat(tier.discount))}% {t("discount")})</span>
            )}
          </div>
        ))}
        <div className="mt-1 font-medium">
          {t("Average Rate:")} <bdi>{money(pricing.averageRate)}</bdi>/{t("msg")}
        </div>
      </div>
    );
  }
  return null;
}

// processingFees: the customer's agreed fee per message type, e.g. { Marketing: 0.05, Service: 0.02 }
function WaCalculator({ processingFees = {} }) {
  const { search } = useLocation();
  const { lang, theme, headless } = readPreferences(search);
  const locale = calculatorLocale(lang);
  const { t, number, money, market } = locale;
  const [selectedCountry, setSelectedCountry] = useState(
    countries.find((c) => c.Market === "Rest of Asia Pacific")
  );
  const [query, setQuery] = useState("");
  const [counts, setCounts] = useState({
    Marketing: 0,
    Utility: 0,
    Authentication: 0,
    Authentication_International: 0,
    Service: 0,
  });
  const [showDetails, setShowDetails] = useState(true);

  // Message counts are whole, non-negative numbers
  const toWholeCount = (value) => Math.max(Math.floor(Number(value)) || 0, 0);

  const setCount = (type, value) =>
    setCounts((prev) => ({
      ...prev,
      [type]: value === "" ? "" : toWholeCount(value),
    }));

  const messageCount = (type) => toWholeCount(counts[type]);

  const isAvailable = (type, country = selectedCountry) =>
    country[type] !== undefined && country[type] !== "N/A";

  // Clear counts for message types the new country does not support
  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    setCounts((prev) =>
      MESSAGE_TYPES.reduce(
        (next, { type }) =>
          isAvailable(type, country) ? next : { ...next, [type]: 0 },
        prev
      )
    );
  };

  // Service messages are billed only above the monthly free allowance
  const billableCount = (type) =>
    type === "Service"
      ? Math.max(messageCount(type) - FREE_SERVICE_MESSAGES, 0)
      : messageCount(type);

  // Meta cost for a single message type
  const metaCost = (type) => {
    const count = billableCount(type);
    if (count === 0 || !isAvailable(type)) {
      return 0;
    }
    const pricing = getCountryPricing(selectedCountry.Market, type, count);
    if (pricing && pricing.totalCost !== undefined) {
      // Tiered calculation returns totalCost
      return pricing.totalCost;
    } else if (pricing && pricing.rate !== undefined) {
      // Flat rate calculation
      return pricing.rate * count;
    }
    return 0;
  };

  const pricingDetails = (type) => {
    const count = billableCount(type);
    if (count === 0 || !isAvailable(type)) {
      return null;
    }
    return getCountryPricing(selectedCountry.Market, type, count);
  };

  const totalCost = () =>
    MESSAGE_TYPES.reduce((sum, { type }) => sum + metaCost(type), 0);

  // Fee is worked out per message type using the customer's agreed fee for that type
  const processingFee = () => {
    const fees = { ...DEFAULT_PROCESSING_FEES, ...processingFees };
    return MESSAGE_TYPES.reduce(
      (sum, { type }) => sum + metaCost(type) * (fees[type] || 0),
      0
    );
  };

  const finalCharge = () => (totalCost() + processingFee()).toFixed(4);

  const isServiceFree = () => messageCount("Service") <= FREE_SERVICE_MESSAGES;

  const filteredCountry =
    query === ""
      ? countries
      : countries.filter((c) => {
          return (
            c.Market.toLowerCase().includes(query.toLowerCase()) ||
            market(c.Market).includes(query) ||
            c.Market.toLocaleLowerCase().includes("rest") ||
            c.Market.toLocaleLowerCase().includes("other")
          );
        });


  return (
    <main className={`wa-calculator min-h-screen flex justify-center${headless ? "" : " sm:px-6 lg:px-8"}`} lang={lang} dir={lang === "ar" ? "rtl" : "ltr"} data-theme={theme}>
      <div className={`w-full${headless ? "" : " max-w-5xl"}`}>
        <div className={`calc-surface grid grid-cols-1 md:grid-cols-2 gap-8${headless ? "" : " py-8 px-4 sm:px-10"}`}>
          <div>
            <div className="space-y-6">
              <div>
                <Combobox value={selectedCountry} onChange={handleCountryChange}>
                <Combobox.Label
                  className="block text-md font-medium calc-text"
                >
                  {t("Your Country")}
                </Combobox.Label>
                  <div className="relative mt-1">
                    <div className="relative w-full cursor-default overflow-hidden rounded-lg calc-surface calc-align border calc-border sm:text-sm">
                      <Combobox.Input
                        className="calc-input shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm calc-border rounded-md"
                        displayValue={(c) => market(c.Market)}
                        onChange={(event) => setQuery(event.target.value)}
                      />
                      <Combobox.Button aria-label={t("Choose country")} className="absolute inset-y-0 calc-end flex items-center calc-pe-2">
                        <ChevronUpDownIcon
                          className="h-5 w-5 calc-muted"
                          aria-hidden="true"
                        />
                      </Combobox.Button>
                    </div>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100 pointer-events-none"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0 pointer-events-none"
                      afterLeave={() => setQuery("")}
                    >
                      <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md calc-surface py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        {filteredCountry.length === 0 && query !== "" ? (
                          <div className="relative cursor-default select-none py-2 px-4 calc-text">
                            {t("Nothing found.")}
                          </div>
                        ) : (
                          filteredCountry.map((c) => (
                            <Combobox.Option
                              key={c.Market}
                              className={({ active }) =>
                                `relative cursor-default select-none py-2 calc-option ${
                                  active
                                    ? "calc-active"
                                    : "calc-text"
                                }`
                              }
                              value={c}
                            >
                              {({ selected, active }) => (
                                <>
                                  <span
                                    className={`block truncate ${
                                      selected ? "font-medium" : "font-normal"
                                    }`}
                                  >
                                    {market(c.Market)}
                                  </span>
                                  {selected ? (
                                    <span
                                      className={`absolute inset-y-0 calc-start flex items-center calc-ps-3 ${
                                        active ? "" : "calc-brand"
                                      }`}
                                    >
                                      <CheckIcon
                                        className="h-5 w-5"
                                        aria-hidden="true"
                                      />
                                    </span>
                                  ) : null}
                                </>
                              )}
                            </Combobox.Option>
                          ))
                        )}
                      </Combobox.Options>
                    </Transition>
                  </div>
                </Combobox>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-4">
              {MESSAGE_TYPES.map(({ type, inputLabel }) => (
                <div key={type}>
                  <label htmlFor={`count-${type}`} className="block text-sm font-medium calc-text">
                    {t(inputLabel)} ({t("monthly")})
                  </label>
                  <div className="mt-1">
                    <input
                      id={`count-${type}`}
                      dir="ltr"
                      aria-describedby={!isAvailable(type) || type === "Service" ? `hint-${type}` : undefined}
                      value={counts[type]}
                      type="number"
                      min="0"
                      step="1"
                      inputMode="numeric"
                      disabled={!isAvailable(type)}
                      className="calc-input shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm calc-border rounded-md "
                      onKeyDown={(e) => {
                        if (["-", "+", ".", ",", "e", "E"].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => setCount(type, e.target.value)}
                    />
                  </div>
                  {!isAvailable(type) && (
                    <p id={`hint-${type}`} className="mt-1 text-xs calc-muted">
                      {t("Not applicable for")} {market(selectedCountry.Market)}
                    </p>
                  )}
                  {type === "Service" && (
                    <p id={`hint-${type}`} className="mt-1 text-xs calc-muted">
                      {locale.serviceHint(number(FREE_SERVICE_MESSAGES))}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="w-full">
            <div>
              <p className="text-md font-medium calc-text">
                {t("Your WhatsApp Business cost per month for")}{" "}
                <b>{market(selectedCountry.Market)} </b>
              </p>
              <dl
                className={`mt-1 grid grid-cols-1 ${
                  showDetails ? "rounded-t-lg" : "rounded-lg"
                } calc-surface overflow-hidden border calc-border`}
              >
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex justify-between">
                    <dt className="text-base font-normal calc-text">
                      {t("Total Message Cost")}
                    </dt>
                    <button
                      type="button"
                      aria-expanded={showDetails}
                      aria-controls="calculator-details"
                      onClick={() => setShowDetails(!showDetails)}
                      className="text-xs cursor-pointer font-normal underline calc-text"
                    >
                      {t(showDetails ? "Hide Details" : "View Details")}
                    </button>
                  </div>
                  <dd className="mt-1 flex justify-between items-baseline md:block lg:flex">
                    <div className="flex items-baseline text-2xl font-semibold calc-muted">
                      <bdi>{money(finalCharge())}</bdi>
                    </div>
                  </dd>
                </div>
              </dl>

              {showDetails ? (
                <dl id="calculator-details" className="grid grid-cols-1 rounded-b-lg calc-surface overflow-hidden border calc-border border-t-0">
                  {MESSAGE_TYPES.map(({ type, costLabel }, index) => (
                    <div
                      key={type}
                      className={`${index === 0 ? "mt-4" : "mt-2"} px-4 flex justify-between gap-4`}
                    >
                      <dt className="text-sm font-normal calc-text">
                        {t(costLabel)}
                        <PricingBreakdown pricing={pricingDetails(type)} locale={locale} />
                        {type === "Service" && !isServiceFree() && (
                          <div className="text-xs calc-muted mt-1">
                            {locale.aboveFree(number(billableCount(type)), number(FREE_SERVICE_MESSAGES))}
                          </div>
                        )}
                      </dt>
                      <dd className="flex justify-between items-baseline md:block lg:flex">
                        {type === "Service" && isServiceFree() ? (
                          <div className="flex items-baseline text-sm calc-success">
                            {t("Free")}
                          </div>
                        ) : (
                          <div className="flex items-baseline text-sm calc-muted">
                            <bdi>{money(metaCost(type))}</bdi>
                          </div>
                        )}
                      </dd>
                    </div>
                  ))}
                  <div className="mt-2 px-4 flex justify-between gap-4">
                    <dt className="text-sm font-normal calc-text">
                      {t("Revora Processing Fee")}
                    </dt>
                    <dd className="flex justify-between items-baseline md:block lg:flex">
                      <div className="flex items-baseline text-sm calc-muted">
                        <bdi>{money(processingFee())}</bdi>
                      </div>
                    </dd>
                  </div>
                  <div className="border-t pt-3 mt-2 mb-3 px-4 flex justify-between gap-4">
                    <dt className="text-sm font-normal calc-text">
                      <b>{t("Total WhatsApp Message Cost")}</b>
                    </dt>
                    <dd className="flex justify-between items-baseline md:block lg:flex">
                      <div className="flex items-baseline text-sm calc-muted">
                      <b><bdi>{money(finalCharge())}</bdi></b>
                      </div>
                    </dd>
                  </div>
                </dl>
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default WaCalculator;
